import { supabase } from "../supabaseClient";

/**
 * Validates if the current user has write access to a specific ledger row
 */
export const canEditTransaction = (tx, currentUserId) => {
  if (!tx || !currentUserId) return false;

  const activeUserId = String(currentUserId);
  const txUserId = tx.user_id ? String(tx.user_id) : null;

  // Verify ownership using the auth_id matching string rule
  const isOwner = txUserId === activeUserId;

  if (!isOwner) return false;

  // Case A: Completely Isolated Personal Expense (No shared transaction group identifier)
  if (!tx.transaction_id) {
    return true;
  }

  // Case B: Sub-expenditures or individual unaccepted debit items
  if (tx.transaction_id) {
    const note = (tx.note || "").toLowerCase();
    
    // Allow edit if it's explicitly an expenditure or a standalone debit item
    if (note.includes("expenditure") || tx.entry_type === "debit") {
      return true;
    }
  }

  // Case C: Shared double-sided peer records remain locked
  return false;
};
/**
 * Validates if the current user has deletion access to a specific ledger row
 */
export const canDeleteTransaction = (tx, currentUserId) => {
  return canEditTransaction(tx, currentUserId);
};

/**
 * Deletes an editable individual row from wallet_ledger (Personal or Sub-Expenditure)
 */
export const deletePersonalExpense = async (ledgerId, currentUserId) => {
  if (!ledgerId) throw new Error("Missing ledger row ID for deletion.");
  if (!currentUserId) throw new Error("User authorization context required.");

  const { error } = await supabase
    .from("wallet_ledger")
    .delete()
    .eq("id", ledgerId)
    .eq("user_id", currentUserId); // Secure enforcement

  if (error) throw error;
  return true;
};

/**
 * Updates an editable individual row in wallet_ledger (Personal or Sub-Expenditure)
 */
export const updatePersonalExpense = async (ledgerId, amount, note, currentUserId) => {
  if (!ledgerId) throw new Error("Missing ledger row ID for update.");
  if (!currentUserId) throw new Error("User authorization context required.");

  const { data, error } = await supabase
    .from("wallet_ledger")
    .update({
      amount: Number(amount),
      note: note?.trim() || ""
    })
    .eq("id", ledgerId)
    .eq("user_id", currentUserId)
    .select();

  if (error) throw error;
  return data;
};

// --- Note on Legacy Peer Functions ---
// deletePendingTransaction and updatePendingTransaction are preserved below 
// ONLY if you intend to edit/delete group items BEFORE they are accepted (status: 'pending').
export const deletePendingTransaction = async (txId, currentUserId) => {
  if (!txId) throw new Error("Missing transaction_id for deletion.");
  const { error } = await supabase.rpc("secure_delete_peer_transaction", {
    p_tx_id: txId,
    p_auth_user_id: currentUserId
  });
  if (error) throw error;
  return true;
};

export const updatePendingTransaction = async (txId, amount, note, currentUserId) => {
  if (!txId) throw new Error("Missing transaction_id for update.");
  const { error } = await supabase.rpc("secure_update_peer_transaction", {
    p_tx_id: txId,
    p_auth_user_id: currentUserId,
    p_amount: Number(amount),
    p_note: note?.trim() || ""
  });
  if (error) throw error;
  return true;
};